import React, { useMemo, useState } from 'react';
import { Building2 } from 'lucide-react';
import EstablishmentGroup from './EstablishmentGroup';
import exportCohortPDF from '../../utils/exportCohortPDF';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * EstablishmentsTab — groups master licenses by their `establishment` text
 * field, then shows each cohort and the apprentices inside. Supervisors that
 * have no establishment set are bucketed into a "Sans établissement" group.
 *
 * 04/06/2026 refactor : the heavy `exportCohortPDF` orchestration was moved
 * to `/utils/exportCohortPDF.js`, and the row rendering was split into
 * `EstablishmentGroup.jsx` so this file stays under ~120 lines.
 */
export default function EstablishmentsTab({ licenses, token, onChanged }) {
  const [expanded, setExpanded] = useState(new Set());
  const [editingMasterId, setEditingMasterId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [exportingCohortId, setExportingCohortId] = useState(null);

  const grouped = useMemo(() => buildGroups(licenses), [licenses]);

  const toggle = (key) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const startEdit = (master) => {
    setEditingMasterId(master.id);
    setEditValue(master.establishment || '');
  };

  const saveEdit = async (master) => {
    try {
      const res = await fetch(`${API}/api/licenses/${master.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ establishment: editValue.trim() }),
      });
      if (res.ok) {
        setEditingMasterId(null);
        onChanged && onChanged();
      }
    } catch (_) { /* silent */ }
  };

  const handleExportCohort = async (master, children) => {
    if (!children || children.length === 0) return;
    setExportingCohortId(master.id);
    try {
      await exportCohortPDF({ master, children, apiBase: API, token });
    } catch (err) {
      console.error('[hst] cohort PDF export failed:', err);
      window.alert("Échec de l'export PDF. Vérifiez votre connexion et réessayez.");
    } finally {
      setExportingCohortId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="establishments-tab">
      <div>
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-emerald-300" /> Établissements
        </h2>
        <p className="text-xs text-slate-500 mt-1">
          Vue hiérarchique : <strong>Établissement</strong> → <strong>Formateurs</strong> → <strong>Apprenants</strong>. Le libellé d&apos;établissement est un champ texte libre sur chaque licence Responsable.
        </p>
      </div>

      {grouped.length === 0 && (
        <div className="bg-ink-800 border border-ink-600 rounded-xl p-8 text-center" data-testid="establishments-empty">
          <Building2 className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-sm text-slate-400">Aucune licence Responsable Pédagogique encore créée.</p>
        </div>
      )}

      {grouped.map(({ key, name, masters: ms, cohorts, learnerCount }) => (
        <EstablishmentGroup
          key={key}
          groupKey={key}
          name={name}
          masters={ms}
          cohorts={cohorts}
          learnerCount={learnerCount}
          isOpen={expanded.has(key)}
          editingMasterId={editingMasterId}
          editValue={editValue}
          exportingCohortId={exportingCohortId}
          onToggle={toggle}
          onStartEdit={startEdit}
          onSaveEdit={saveEdit}
          onCancelEdit={() => setEditingMasterId(null)}
          onChangeEditValue={setEditValue}
          onExportCohort={handleExportCohort}
        />
      ))}
    </div>
  );
}

/* ── Pure helper : group licenses by establishment ───────────────────── */
function buildGroups(licenses) {
  const masters = licenses.filter(l => l.is_master);
  const apprentices = licenses.filter(l => !l.is_master);
  const map = {};
  masters.forEach(m => {
    const raw = (m.establishment || '').trim();
    const key = raw || '__none__';
    map[key] = map[key] || { name: raw, masters: [] };
    map[key].masters.push(m);
  });
  return Object.entries(map)
    .map(([key, { name, masters: ms }]) => {
      let learnerCount = 0;
      const cohorts = ms.map(m => {
        const children = apprentices.filter(a => a.master_license_id === m.id);
        learnerCount += children.length;
        return { master: m, children };
      });
      return { key, name, masters: ms, cohorts, learnerCount };
    })
    .sort((a, b) => {
      if (a.key === '__none__') return 1;
      if (b.key === '__none__') return -1;
      return (a.name || '').localeCompare(b.name || '', 'fr');
    });
}
